<template>
    <span>
        <!-- Selected items UI -->
        <q-btn
            @click="openDefectDialog"
            icon="far fa-exclamation-circle"
            color="negative"
            class="phase-table-button phase-table-button__edit q-mr-xs q-px-sm"
            :label="$t('Missing defect')"
            :dense="$q.screen.gt.md"
            flat
            outline
        >
            <q-tooltip>
                {{ $t('Click here to add a defect') }}
            </q-tooltip>
        </q-btn>
        <!-- Dialog -->
        <q-dialog v-model="showDialog">
            <q-card
                class="dialog"
                :class="$q.screen.gt.lg ? 'dialog__lg' : 'dialog__xs'"
            >
                <!-- Dialog header -->
                <q-bar class="bg-primary">
                    <div class="text-white">
                        {{ dialogTitle }}
                    </div>

                    <q-space />

                    <q-btn
                        :disable="isLoading"
                        @click="closeDialog"
                        class="text-white"
                        icon="close"
                        dense
                        flat
                    >
                        <q-tooltip>{{ $t('Close') }}</q-tooltip>
                    </q-btn>
                </q-bar>

                <!-- Table -->
                <q-card-section>
                    <q-input
                        v-model="search"
                        @clear="search = ''"
                        :placeholder="searchLabel"
                        color="accent"
                        class="q-mb-sm"
                        debounce="500"
                        clearable
                        outlined
                        dense
                        style="width: 100%"
                    />
                    <q-table
                        @request="onRequest"
                        :rows="defectList"
                        :columns="columns"
                        :filter="search"
                        v-model:pagination="pagination"
                        :rows-per-page-options="[30]"
                        v-model:selected="selected"
                        :loading="isLoading"
                        :row-key="rowKey"
                        :no-data-label="noDataLabel || $t('No data available')"
                        :grid="cardMode"
                        table-class="sticky-table table-mode"
                        card-container-class="card-mode"
                        selection="single"
                        binary-state-sort
                        bordered
                        flat
                    >
                        <!-- Card Mode -->
                        <template v-slot:item="props">
                            <slot name="item" :props="props"></slot>
                        </template>
                    </q-table>
                </q-card-section>

                <!-- Dialog actions -->
                <q-card-section
                    v-if="!loadingOptionsFailed"
                    class="q-px-lg q-pb-md float-right"
                >
                    <q-btn
                        @click="closeDialog"
                        :disable="isLoading"
                        class="q-mr-md"
                        color="white"
                        text-color="primary"
                        outline
                        >{{ $t('Cancel') }}</q-btn
                    >
                    <q-btn
                        :disable="
                            isLoading || $userCannot('edit', 'quality_events')
                        "
                        :label="$t('Save')"
                        @click="confirm"
                        color="primary"
                        :loading="isLoading"
                    />
                </q-card-section>
            </q-card>
        </q-dialog>
    </span>
</template>

<script>
import TableSelectMixin from '@mixins/TableSelectMixin.vue';
import UpdateURL from '@mixins/UpdateURL.vue';

export default {
    name: 'MissingDefectLabel',
    mixins: [TableSelectMixin, UpdateURL],
    emits: ['refreshList', 'clearSelection', 'defectJustified'],
    props: {
        eventId: {
            type: Number,
            require: true
        },
        assetId: {
            type: Number,
            required: false
        },
        baseURL: {
            type: String,
            required: true
        },
        queryParams: {
            type: Object,
            required: false
        }
    },
    data() {
        return {
            isLoading: false,
            pagination: {
                sortBy: 'start',
                descending: true,
                page: 1,
                rowsPerPage: 30,
                rowsNumber: null
            },
            loadingOptionsFailed: false,
            defectList: []
        };
    },
    watch: {
        /**
         * Updates the list of defects when the user types
         * on the search bar
         */
        search() {
            this.getDefects();
        }
    },
    computed: {
        /**
         * Get the selected defect id from the 'value' mixin prop
         * @return {Number}
         */
        selectedDefectId() {
            if (this.modelValue) {
                return this.modelValue.value;
            }
            return null;
        }
    },
    methods: {
        /**
         * Overrides onRequest method (mixin) to avoid multiple API calls
         */
        async onRequest() {
            this.pagination['rowsNumber'] = this.defectList.count;
        },
        /**
         * Opens defects dialog and load the list of defects for this
         */
        openDefectDialog() {
            this.showDialog = true;
            this.getDefects();
        },
        /**
         * Get list of defects and update the list based on the search.
         */
        async getDefects() {
            this.isLoading = true;
            try {
                const response = await this.$axios.get(this.url);
                // empty the current list of defects
                this.defectList = [];

                // populate the list with the defects from the response
                const defects = response.data.results;
                defects.forEach(defect => {
                    this.defectList.push({
                        id: defect.id,
                        facility: defect.facility,
                        name: defect.name,
                        code: defect.code,
                        description: defect.description
                    });
                });

                // update total items count
                this.pagination.rowsNumber = response.data.count;
                // toggle loading options in the case that there was previously an error
                this.loadingOptionsFailed = false;
            } catch (error) {
                this.loadingOptionsFailed = true;
            } finally {
                this.isLoading = false;
            }
        },
        /**
         * Get defects filtered by search params.
         */
        async confirm() {
            // Check if a defect was selected
            if (this.selectedDefectId === null) {
                return this.$notifyError(
                    this.$t('Failed to update. Please select a defect.')
                );
            }

            // Disable submit button and start loading animation
            this.isLoading = true;

            const payload = {
                defect: this.selectedDefectId
            };
            const errorMessage = this.$t(
                'Failed to update defect at this time. Please try again later.'
            );
            try {
                await this.$axios.patch(
                    `quality_events/${this.eventId}/`,
                    payload
                );
                // notify user
                this.$notifySuccess(
                    this.$t('The defect has been successfully updated.')
                );
                // Request updated data for the parent
                this.$emit('refreshList');
                // Confirm that a defect was justified to prevent unnecessary requests
                this.$emit('defectJustified');
            } catch (error) {
                this.$notifyError(errorMessage, error);
            }

            // Enable submit button
            this.isLoading = false;

            this.closeDialog();
        },
        /**
         * Clear the selected defect and close the dialog using TableSelectMixin method
         */
        closeDialog() {
            // Next time the dialog opens the search input will be empty
            this.search = '';
            this.$emit('clearSelection');
            this.close();
        }
    }
};
</script>

<style lang="scss" scoped>
.dialog__lg {
    width: 50%;
    max-width: none;
}
.dialog__xs {
    width: 100%;
    max-width: none;
}
// scroll area for the cards container of the component
:deep(.card-mode) {
    max-height: 60vh;
    overflow-y: scroll;
}
// scroll area for the table rows part of the component
:deep(.table-mode) {
    height: 55vh;
}
// Missing defect button
.phase-table-button :deep(.q-btn__wrapper) {
    padding: 2px;
    font-size: 12px;
}
.phase-table-button {
    min-width: 150px;
}
</style>
